// Appointments Management Component
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Select,
  MenuItem as SelectMenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tabs,
  Tab,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  MoreVert,
  CalendarMonth,
  Refresh,
  AccessTime,
  CheckCircle,
  TableChart,
  CalendarToday,
} from "@mui/icons-material";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import apiService from "../../services/api";
import authService from "../../services/auth";
import { APPOINTMENT_STATUS, APPOINTMENT_STATUS_LIST } from "../../constants";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import FeatureGuard from "../common/FeatureGuard";
import PermissionWrapper from "../common/PermissionWrapper";

// Setup the localizer for react-big-calendar
const localizer = momentLocalizer(moment);

const Appointments = () => {
  const { formatPrice } = useCurrency();
  const {
    formatDate: formatDateTZ,
    formatTime: formatTimeTZ,
    getCurrentDate,
    isToday,
  } = useTimezone();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [deletingAppointment, setDeletingAppointment] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [viewMode, setViewMode] = useState("calendar"); // 'calendar' or 'table'
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [userPermissions, setUserPermissions] = useState(null);
  const defaultStatusFilter = [
    APPOINTMENT_STATUS.SCHEDULED,
    APPOINTMENT_STATUS.CONFIRMED,
    APPOINTMENT_STATUS.IN_PROGRESS,
  ];
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const [formData, setFormData] = useState({
    client_id: "",
    service_id: "",
    appointment_date: moment().format("YYYY-MM-DD"),
    appointment_time: moment().format("HH:mm"),
    status: APPOINTMENT_STATUS.SCHEDULED,
    notes: "",
    mobile: "",
    client_name: "",
    is_existing_client: false,
  });

  useEffect(() => {
    fetchData();
    fetchPermissions();
  }, []);

  const isAdminLike = (user) =>
    String(user?.role_name || "").toLowerCase().includes("admin");

  const hasPermission = (module, action = "view") => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false;
    return userPermissions?.[module]?.[action] === true;
  };

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions
      setUserPermissions({});
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsRes, clientsRes, servicesRes] = await Promise.all([
        apiService.get("/appointments"),
        apiService.get("/clients"),
        apiService.get("/tenant-services"),
      ]);

      if (appointmentsRes.success)
        setAppointments(appointmentsRes.data.appointments || []);
      if (clientsRes.success) setClients(clientsRes.data.clients || []);
      if (servicesRes.success) setServices(servicesRes.data.services || []);
    } catch (err) {
      setError("Error fetching data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const toggleStatusFilter = (statusValue) => {
    setStatusFilter((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(statusValue)
        ? current.filter((v) => v !== statusValue)
        : [...current, statusValue];
    });
  };

  const getFilteredAppointments = () => {
    const q = String(searchTerm || "").trim().toLowerCase();
    const qDigits = q.replace(/\D/g, "");

    let filtered = (appointments || []).filter((appointment) => {
      if (!q) return true;

      const client = (clients || []).find(
        (c) => Number(c.id) === Number(appointment.client_id)
      );
      const service = (services || []).find(
        (s) => Number(s.id) === Number(appointment.service_id)
      );
      const clientName = client
        ? `${client.first_name} ${client.last_name}`
        : "";
      const serviceName = service ? service.name : "";

      return (
        clientName.toLowerCase().includes(q) ||
        serviceName.toLowerCase().includes(q) ||
        String(appointment.appointment_date || "").toLowerCase().includes(q) ||
        // Number-friendly search:
        (qDigits &&
          (String(client?.mobile || "").includes(qDigits) ||
            String(appointment?.id || "").includes(qDigits) ||
            String(appointment?.appointment_number || "")
              .replace(/\D/g, "")
              .includes(qDigits) ||
            String(appointment?.appointment_no || "")
              .replace(/\D/g, "")
              .includes(qDigits)))
      );
    });

    // Filter by tab using timezone-aware date comparison
    const today = getCurrentDate();
    switch (tabValue) {
      case 0: // Today
        filtered = filtered.filter((apt) => isToday(apt.appointment_date));
        break;
      case 1: // Upcoming
        filtered = filtered.filter((apt) => apt.appointment_date > today);
        break;
      case 2: // Past
        filtered = filtered.filter((apt) => apt.appointment_date < today);
        break;
      case 3: // All
      default:
        break;
    }

    // Filter by selected statuses
    const selectedStatuses = Array.isArray(statusFilter) ? statusFilter : [];
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((apt) => selectedStatuses.includes(apt.status));
    } else {
      filtered = [];
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
      const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
      return dateA - dateB;
    });
  };

  const getTabCounts = () => {
    const today = getCurrentDate();
    return {
      today: (appointments || []).filter((apt) => isToday(apt.appointment_date))
        .length,
      upcoming: (appointments || []).filter(
        (apt) => apt.appointment_date > today
      ).length,
      past: (appointments || []).filter((apt) => apt.appointment_date < today)
        .length,
      all: (appointments || []).length,
    };
  };

  // Convert appointments to calendar events
  const getCalendarEvents = () => {
    const selectedStatuses = Array.isArray(statusFilter) ? statusFilter : [];
    const visibleAppointments =
      selectedStatuses.length > 0
        ? (appointments || []).filter((a) => selectedStatuses.includes(a.status))
        : [];

    return visibleAppointments.map((appointment) => {
      const client = getClient(appointment.client_id);
      const service = getService(appointment.service_id);
      const clientName = client
        ? `${client.first_name} ${client.last_name}`
        : "Unknown Client";
      const serviceName = service?.name || "Unknown Service";

      // Create start and end dates
      const startDate = new Date(
        `${appointment.appointment_date}T${appointment.appointment_time}`
      );
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + (service?.duration || 60));

      return {
        id: appointment.id,
        title: ` ${formatTime(
          appointment.appointment_time
        )} ${clientName} - ${serviceName}`,
        start: startDate,
        end: endDate,
        time: appointment.appointment_time,
        resource: appointment,
        allDay: false,
      };
    });
  };

  const handleOpenDialog = (appointment = null) => {
    // Permission checks (UI-level). API also enforces this.
    if (appointment && !hasPermission("appointments", "edit")) {
      setError("Permission denied");
      return;
    }
    if (!appointment && !hasPermission("appointments", "add")) {
      setError("Permission denied");
      return;
    }

    setDialogError(""); // Clear any previous dialog errors
    if (appointment) {
      const client = getClient(appointment.client_id);
      setEditingAppointment(appointment);
      setFormData({
        client_id: appointment.client_id,
        service_id: appointment.service_id,
        appointment_date: appointment.appointment_date,
        appointment_time: appointment.appointment_time,
        status: appointment.status,
        notes: appointment.notes || "",
        mobile: client?.mobile || "",
        client_name: client ? `${client.first_name} ${client.last_name}` : "",
        is_existing_client: true,
      });
    } else {
      setEditingAppointment(null);
      // Auto-populate with current date and time for new appointments
      const currentDate = moment().format('YYYY-MM-DD');
      const currentTime = moment().format('HH:mm');
      
      setFormData({
        client_id: "",
        service_id: "",
        appointment_date: currentDate,
        appointment_time: currentTime,
        status: APPOINTMENT_STATUS.SCHEDULED,
        notes: "",
        mobile: "",
        client_name: "",
        is_existing_client: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAppointment(null);
    setDialogError(""); // Clear dialog errors when closing
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // Check for existing client when mobile number is entered
    if (name === "mobile" && value.length >= 10) {
      checkExistingClient(value);
    } else if (name === "mobile" && value.length < 10) {
      // Clear client data if mobile is less than 10 digits
      setFormData((prev) => ({
        ...prev,
        mobile: value,
        client_id: "",
        client_name: "",
        is_existing_client: false,
      }));
    }
  };

  const checkExistingClient = async (mobile) => {
    try {
      const existingClient = clients.find((client) => client.mobile === mobile);
      if (existingClient) {
        setFormData((prev) => ({
          ...prev,
          client_id: existingClient.id,
          client_name: `${existingClient.first_name} ${existingClient.last_name}`,
          is_existing_client: true,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          client_id: "",
          client_name: "",
          is_existing_client: false,
        }));
      }
    } catch (err) {
      console.error("Error checking existing client:", err);
    }
  };

  const handleSubmit = async () => {
    try {
      setDialogError(""); // Clear any previous dialog errors

      // Permission checks (UI-level). API also enforces this.
      if (editingAppointment && !hasPermission("appointments", "edit")) {
        setDialogError("Permission denied");
        return;
      }
      if (!editingAppointment && !hasPermission("appointments", "add")) {
        setDialogError("Permission denied");
        return;
      }

      // Validate required fields
      if (!formData.mobile || formData.mobile.length < 10) {
        setDialogError(
          "Please enter a valid mobile number (minimum 10 digits)"
        );
        return;
      }

      if (!formData.client_name && !formData.is_existing_client) {
        setDialogError("Please enter client name");
        return;
      }

      let clientId = formData.client_id;

      // Create new client if not existing
      if (!formData.is_existing_client && !editingAppointment) {
        // Creating a new client requires clients.add
        if (!hasPermission("clients", "add")) {
          setDialogError("Permission denied");
          return;
        }

        const nameParts = formData.client_name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const clientData = {
          first_name: firstName,
          last_name: lastName,
          mobile: formData.mobile,
          email: "", // Optional
        };

        const clientResponse = await apiService.post("/clients", clientData);
        console.log("Client creation response:", clientResponse); // Debug log
        console.log(
          "Client response structure:",
          JSON.stringify(clientResponse, null, 2)
        ); // Detailed debug

        // Check if response indicates success (either explicit success field or HTTP 200/201 status)
        const isSuccessful =
          clientResponse.success === true ||
          clientResponse.status === "success" ||
          (clientResponse.status >= 200 && clientResponse.status < 300) ||
          (!clientResponse.hasOwnProperty("success") &&
            !clientResponse.error &&
            !clientResponse.message);

        if (isSuccessful) {
          // Handle different possible response structures
          clientId =
            clientResponse.data?.client?.id ||
            clientResponse.data?.id ||
            clientResponse.client?.id ||
            clientResponse.id ||
            clientResponse.data?.insertId || // Handle MySQL insert ID
            clientResponse.insertId || // Direct insert ID
            clientResponse.data?.client_id ||
            clientResponse.client_id;

          console.log("Extracted clientId:", clientId); // Debug extracted ID

          if (!clientId) {
            console.error("Client ID not found in response:", clientResponse);
            console.error(
              "Available response keys:",
              Object.keys(clientResponse)
            );
            if (clientResponse.data) {
              console.error(
                "Available data keys:",
                Object.keys(clientResponse.data)
              );
            }

            // Try to extract any numeric ID from the response as a last resort
            const possibleIds = Object.values(clientResponse).filter(
              (val) => typeof val === "number" && val > 0
            );
            if (possibleIds.length > 0) {
              clientId = possibleIds[0];
              console.log("Using fallback ID:", clientId);
            } else {
              setDialogError(
                "Failed to create client - could not extract client ID from response"
              );
              return;
            }
          }

          // Refresh clients list
          await fetchData();
        } else {
          console.error("Client creation failed:", clientResponse);
          const errorMessage =
            clientResponse.message ||
            clientResponse.error ||
            clientResponse.data?.message ||
            clientResponse.data?.error ||
            "Failed to create client";
          setDialogError(errorMessage);
          return;
        }
      }

      const appointmentData = {
        client_id: clientId,
        service_id: formData.service_id,
        appointment_date: formData.appointment_date,
        appointment_time: formData.appointment_time,
        status: formData.status,
        notes: formData.notes,
      };

      let response;
      if (editingAppointment) {
        response = await apiService.put(
          `/appointments/${editingAppointment.id}`,
          appointmentData
        );
      } else {
        response = await apiService.post("/appointments", appointmentData);
      }

      if (response.success) {
        fetchData();
        handleCloseDialog();
        setSuccessMessage(
          editingAppointment
            ? "Appointment updated successfully!"
            : "Appointment booked successfully!"
        );
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setDialogError(response.message || "Failed to save appointment");
      }
    } catch (err) {
      setDialogError("Error saving appointment: " + err.message);
    }
  };

  const handleMenuOpen = (event, appointment) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAppointment(null);
  };

  const handleRequestDeleteAppointment = () => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("appointments", "delete")) {
      setError("Permission denied");
      handleMenuClose();
      return;
    }

    if (!selectedAppointment) {
      handleMenuClose();
      return;
    }

    setAppointmentToDelete(selectedAppointment);
    setDeleteConfirmOpen(true);
    // Close menu without losing the delete target
    setAnchorEl(null);
    setSelectedAppointment(null);
  };

  const handleCancelDeleteAppointment = () => {
    setDeleteConfirmOpen(false);
    setAppointmentToDelete(null);
    setDeletingAppointment(false);
  };

  const handleConfirmDeleteAppointment = async () => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("appointments", "delete")) {
      setError("Permission denied");
      handleCancelDeleteAppointment();
      return;
    }

    if (!appointmentToDelete) return;

    try {
      setDeletingAppointment(true);
      const response = await apiService.delete(
        `/appointments/${appointmentToDelete.id}`
      );

      if (response.success) {
        fetchData();
        setSuccessMessage("Appointment deleted successfully!");
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(""), 5000);
        handleCancelDeleteAppointment();
      } else {
        setError(response.message || "Failed to delete appointment");
        setDeletingAppointment(false);
      }
    } catch (err) {
      setError("Error deleting appointment: " + err.message);
      setDeletingAppointment(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("appointments", "edit")) {
      setError("Permission denied");
      handleMenuClose();
      return;
    }

    try {
      const response = await apiService.put(`/appointments/${appointmentId}`, {
        status: newStatus,
      });
      if (response.success) {
        fetchData();
        setSuccessMessage("Appointment status updated successfully!");
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setError("Failed to update status");
      }
    } catch (err) {
      setError("Error updating status: " + err.message);
    }
    handleMenuClose();
  };

  const getClient = (clientId) => {
    return clients.find((c) => c.id === clientId);
  };

  const getService = (serviceId) => {
    return services.find((s) => s.id === serviceId);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  // Calendar navigation handlers
  const handleNavigate = (newDate) => {
    setCalendarDate(newDate);
  };

  const handleView = (view) => {
    setCalendarView(view);
  };

  // Calendar event handlers
  const handleSelectEvent = (event) => {
    setSelectedAppointment(event.resource);
    handleOpenDialog(event.resource);
  };

  const handleSelectSlot = ({ start }) => {
    const formattedDate = moment(start).format("YYYY-MM-DD");
    const formattedTime = moment(start).format("HH:mm");

    setFormData({
      client_id: "",
      service_id: "",
      appointment_date: formattedDate,
      appointment_time: formattedTime,
      status: APPOINTMENT_STATUS.SCHEDULED,
      notes: "",
      mobile: "",
      client_name: "",
      is_existing_client: false,
    });
    handleOpenDialog();
  };

  // Custom event style getter for calendar
  const eventStyleGetter = (event) => {
    const appointment = event.resource;
    let backgroundColor = "#3174ad"; // default blue

    switch (appointment.status) {
      case APPOINTMENT_STATUS.SCHEDULED:
        backgroundColor = "#FFC107"; // Scheduled: yellow
        break;
      case APPOINTMENT_STATUS.CONFIRMED:
        backgroundColor = "#2196F3"; // Confirmed: blue
        break;
      case APPOINTMENT_STATUS.IN_PROGRESS:
        backgroundColor = "#9C27B0"; // In Progress: purple
        break;
      case APPOINTMENT_STATUS.COMPLETED:
        backgroundColor = "#4CAF50"; // Completed: green
        break;
      case APPOINTMENT_STATUS.CANCELLED:
        backgroundColor = "#F44336"; // Cancelled: red
        break;
      case APPOINTMENT_STATUS.NO_SHOW:
        backgroundColor = "#9E9E9E"; // No Show: grey
        break;
      default:
        backgroundColor = "#9e9e9e"; // grey
    }

    return {
      style: {
        backgroundColor,
        borderRadius: "5px",
        opacity: 0.8,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hourStr, minuteStr] = time.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || "00";
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute.padStart(2, "0")} ${ampm}`;
  };

  const formatDate = (date) => {
    return formatDateTZ(date);
  };

  const getStatusColor = (status) => {
    const statusOption = APPOINTMENT_STATUS_LIST.find(
      (opt) => opt.value === status
    );
    return statusOption ? statusOption.color : "default";
  };

  const tabCounts = getTabCounts();
  const filteredAppointments = getFilteredAppointments();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading appointments...</Typography>
      </Box>
    );
  }

  return (
    <FeatureGuard feature="Appointment Booking">
      <Box>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" fontWeight="bold">
            Appointments
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newView) => newView && setViewMode(newView)}
              size="small"
            >
              <ToggleButton value="calendar">
                <Tooltip title="Calendar View">
                  <CalendarToday />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="table">
                <Tooltip title="Table View">
                  <TableChart />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
            >
              Refresh
            </Button>
            <PermissionWrapper permission="appointments.add">
              <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Book Appointment
            </Button>
            </PermissionWrapper>
          </Box>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Status Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Status
            </Typography>
            <FormGroup row>
              {APPOINTMENT_STATUS_LIST.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={(statusFilter || []).includes(opt.value)}
                      onChange={() => toggleStatusFilter(opt.value)}
                      size="small"
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <Paper sx={{ p: 2, mb: 3 }}>
          {/* Status Legend */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, color: "#666" }}
            >
              Status Legend:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#FFC107",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  Scheduled
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#2196F3",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  Confirmed
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#9C27B0",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  In Progress
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#4CAF50",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  Completed
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#F44336",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  Cancelled
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#9E9E9E",
                    borderRadius: "4px",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  No Show
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ height: 600 }}>
            <Calendar
              localizer={localizer}
              events={getCalendarEvents()}
              startAccessor="start"
              endAccessor="end"
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              onNavigate={handleNavigate}
              onView={handleView}
              date={calendarDate}
              view={calendarView}
              selectable
              eventPropGetter={eventStyleGetter}
              views={["month", "week", "day"]}
              step={30}
              showMultiDayTimes
              components={{
                toolbar: ({ label, onNavigate, onView, view }) => (
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2,
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    {/* Navigation Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onNavigate("PREV")}
                        sx={{
                          minWidth: "80px",
                          fontWeight: 600,
                          borderColor: "#f06292",
                          color: "#f06292",
                          "&:hover": {
                            backgroundColor: "#fce4ec",
                            borderColor: "#e91e63",
                          },
                        }}
                      >
                        ‹ Prev
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => onNavigate("TODAY")}
                        sx={{
                          minWidth: "80px",
                          fontWeight: 600,
                          backgroundColor: "#f06292",
                          "&:hover": {
                            backgroundColor: "#e91e63",
                          },
                        }}
                      >
                        Today
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onNavigate("NEXT")}
                        sx={{
                          minWidth: "80px",
                          fontWeight: 600,
                          borderColor: "#f06292",
                          color: "#f06292",
                          "&:hover": {
                            backgroundColor: "#fce4ec",
                            borderColor: "#e91e63",
                          },
                        }}
                      >
                        Next ›
                      </Button>
                    </Box>

                    {/* Current Date/Period Label */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        textAlign: "center",
                        flex: 1,
                        color: "#f06292",
                        fontSize: { xs: "1rem", md: "1.25rem" },
                      }}
                    >
                      {label}
                    </Typography>

                    {/* View Controls */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant={view === "month" ? "contained" : "outlined"}
                        size="small"
                        onClick={() => onView("month")}
                        sx={{
                          minWidth: "70px",
                          fontWeight: 600,
                          ...(view === "month"
                            ? {
                                backgroundColor: "#f06292",
                                "&:hover": { backgroundColor: "#e91e63" },
                              }
                            : {
                                borderColor: "#f06292",
                                color: "#f06292",
                                "&:hover": {
                                  backgroundColor: "#fce4ec",
                                  borderColor: "#e91e63",
                                },
                              }),
                        }}
                      >
                        Month
                      </Button>
                      <Button
                        variant={view === "week" ? "contained" : "outlined"}
                        size="small"
                        onClick={() => onView("week")}
                        sx={{
                          minWidth: "70px",
                          fontWeight: 600,
                          ...(view === "week"
                            ? {
                                backgroundColor: "#f06292",
                                "&:hover": { backgroundColor: "#e91e63" },
                              }
                            : {
                                borderColor: "#f06292",
                                color: "#f06292",
                                "&:hover": {
                                  backgroundColor: "#fce4ec",
                                  borderColor: "#e91e63",
                                },
                              }),
                        }}
                      >
                        Week
                      </Button>
                      <Button
                        variant={view === "day" ? "contained" : "outlined"}
                        size="small"
                        onClick={() => onView("day")}
                        sx={{
                          minWidth: "70px",
                          fontWeight: 600,
                          ...(view === "day"
                            ? {
                                backgroundColor: "#f06292",
                                "&:hover": { backgroundColor: "#e91e63" },
                              }
                            : {
                                borderColor: "#f06292",
                                color: "#f06292",
                                "&:hover": {
                                  backgroundColor: "#fce4ec",
                                  borderColor: "#e91e63",
                                },
                              }),
                        }}
                      >
                        Day
                      </Button>
                    </Box>
                  </Box>
                ),
                event: ({ event }) => (
                  <Box sx={{ fontSize: "0.75rem", p: 0.5, color: "#000" }}>
                    {event.title} {/* Service name */}
                  </Box>
                ),
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
            >
              <Tab
                label={
                  <Badge badgeContent={tabCounts.today} color="primary">
                    Today
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge badgeContent={tabCounts.upcoming} color="primary">
                    Upcoming
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge badgeContent={tabCounts.past} color="default">
                    Past
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge badgeContent={tabCounts.all} color="default">
                    All
                  </Badge>
                }
              />
            </Tabs>
          </Box>

          {/* Search */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search appointments by client name, service, or date..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>

          {/* Appointments Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAppointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={4}>
                        <CalendarMonth
                          sx={{ fontSize: 64, color: "grey.400", mb: 2 }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          {searchTerm
                            ? "No appointments found matching your search"
                            : "No appointments scheduled"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {searchTerm
                            ? "Try adjusting your search terms"
                            : "Book your first appointment to get started"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAppointments.map((appointment) => {
                    const client = getClient(appointment.client_id);
                    const service = getService(appointment.service_id);

                    return (
                      <TableRow key={appointment.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                              {client
                                ? getInitials(
                                    client.first_name,
                                    client.last_name
                                  )
                                : "?"}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {client
                                  ? `${client.first_name} ${client.last_name}`
                                  : "Unknown Client"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {client?.mobile || "No phone"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {service?.name || "Unknown Service"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {service
                                ? `${formatPrice(service.price)} • ${
                                    service.duration
                                  } min`
                                : ""}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box display="flex" alignItems="center" mb={0.5}>
                              <CalendarMonth
                                sx={{
                                  fontSize: 16,
                                  mr: 1,
                                  color: "text.secondary",
                                }}
                              />
                              <Typography variant="body2">
                                {formatDate(appointment.appointment_date)}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                              <AccessTime
                                sx={{
                                  fontSize: 16,
                                  mr: 1,
                                  color: "text.secondary",
                                }}
                              />
                              <Typography variant="body2">
                                {formatTime(appointment.appointment_time)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              APPOINTMENT_STATUS_LIST.find(
                                (opt) => opt.value === appointment.status
                              )?.label || appointment.status
                            }
                            color={getStatusColor(appointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {appointment.notes || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, appointment)}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >

        <PermissionWrapper permission="appointments.edit">
        <MenuItem
          onClick={() => {
            handleOpenDialog(selectedAppointment);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} /> Edit
        </MenuItem>
        </PermissionWrapper>
        {selectedAppointment?.status !== APPOINTMENT_STATUS.COMPLETED && (
          <MenuItem
            onClick={() =>
              handleStatusChange(
                selectedAppointment.id,
                APPOINTMENT_STATUS.COMPLETED
              )
            }
          >
            <CheckCircle sx={{ mr: 1 }} /> Mark Completed
          </MenuItem>
        )}
        <PermissionWrapper permission="appointments.delete">
        <MenuItem
          onClick={handleRequestDeleteAppointment}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 1 }} /> Delete
        </MenuItem>
        </PermissionWrapper>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!deletingAppointment) handleCancelDeleteAppointment();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete appointment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Typography>
          <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Client:</strong>{" "}
              {(() => {
                const client = getClient(appointmentToDelete?.client_id);
                return client
                  ? `${client.first_name} ${client.last_name}`.trim()
                  : "Unknown Client";
              })()}
            </Typography>
            <Typography variant="body2">
              <strong>Date:</strong> {appointmentToDelete?.appointment_date || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Time:</strong> {appointmentToDelete?.appointment_time || "-"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteAppointment} disabled={deletingAppointment}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteAppointment}
            variant="contained"
            color="error"
            disabled={deletingAppointment}
          >
            {deletingAppointment ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAppointment ? "Edit Appointment" : "Book New Appointment"}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDialogError("")}
            >
              {dialogError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            {/* Row 1: Mobile + Client Name */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobile"
                type="tel"
                value={formData.mobile}
                onChange={handleInputChange}
                required
                inputProps={{
                  minLength: 10,
                  pattern: "[0-9]*",
                }}
                helperText="Enter minimum 10 digits"
              />
              <TextField
                fullWidth
                label="Client Name"
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                required
                InputProps={{
                  readOnly: formData.is_existing_client,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: formData.is_existing_client
                      ? "#f8f9fa"
                      : "transparent",
                    color: formData.is_existing_client ? "#495057" : "inherit",
                  },
                  "& .MuiFormHelperText-root": {
                    marginTop: "8px",
                    marginLeft: "4px",
                    fontSize: "0.75rem",
                  },
                }}
                helperText={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {formData.is_existing_client && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#28a745",
                          display: "inline-block",
                        }}
                      />
                    )}
                    {formData.is_existing_client
                      ? "Existing client found"
                      : "Enter full name for new client"}
                  </Box>
                }
              />
            </Box>

            {/* Row 2: Service */}

            {/* Row 3: Date + Time */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Date"
                name="appointment_date"
                type="date"
                value={formData.appointment_date}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Time"
                name="appointment_time"
                type="time"
                value={formData.appointment_time}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Service</InputLabel>
                <Select
                  name="service_id"
                  value={formData.service_id}
                  label="Service"
                  onChange={handleInputChange}
                >
                  {services.map((service) => (
                    <SelectMenuItem key={service.id} value={service.id}>
                      {service.name} - {formatPrice(service.price)}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange}
                >
                  {APPOINTMENT_STATUS_LIST.map((option) => (
                    <SelectMenuItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Row 5: Notes (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any special notes for this appointment..."
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAppointment ? "Update" : "Book"} Appointment
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </FeatureGuard>
  );
};

export default Appointments;
